import {
  ACTIVE_RUN,
  selectTargetThread,
  type AgentState,
  type TargetKey,
  type ThreadState,
  type WireMessage,
} from "@/agent";

import { plainText } from "./plainText";

/**
 * What the lip's bar shows, one primitive per selector, so the bar re-renders only when its own
 * value changes (never on a delta, except the preview line).
 */
type State = { agent: AgentState };

export type LipActivity = "thinking" | "writing" | null;

/** The reply text streaming now; none while the buffer has a gap (the typing indicator shows). */
function liveText(thread: ThreadState | undefined): string | undefined {
  if (!thread?.live || thread.live.gap) return undefined;
  return Object.values(thread.live.parts).findLast((part) => part.kind === "text" && part.text)
    ?.text;
}

export function selectLipActivity(state: State, key: TargetKey): LipActivity {
  const thread = selectTargetThread(state, key);
  if (!thread?.run || !ACTIVE_RUN.includes(thread.run.status)) return null;
  return liveText(thread) ? "writing" : "thinking";
}

export function selectHasPendingChoice(state: State, key: TargetKey): boolean {
  const thread = selectTargetThread(state, key);
  return !!thread && Object.values(thread.choices).some((choice) => choice.status === "pending");
}

/** An answer is an assistant message with text: one per reply, however many tool steps it took. */
const isAnswer = (message: WireMessage) =>
  message.role === "assistant" && message.parts.some((part) => part.kind === "text");

function lastAnswer(thread: ThreadState | undefined): WireMessage | undefined {
  return thread?.messages.findLast(isAnswer);
}

export function selectLastAnswerSeq(state: State, key: TargetKey): number {
  return lastAnswer(selectTargetThread(state, key))?.seq ?? 0;
}

export function selectAnswersAfter(state: State, key: TargetKey, seq: number): number {
  const messages = selectTargetThread(state, key)?.messages ?? [];
  return messages.filter((message) => message.seq > seq && isAnswer(message)).length;
}

/** The newest assistant text: the reply streaming now, else the last one stored. */
export function selectLipPreview(state: State, key: TargetKey): string {
  const thread = selectTargetThread(state, key);
  const live = liveText(thread);
  if (live) return plainText(live);
  const part = lastAnswer(thread)?.parts.findLast((part) => part.kind === "text");
  return part?.kind === "text" ? plainText(part.text) : "";
}
